import en from '../translations/en.json';
import de from '../translations/de.json';
import fr from '../translations/fr.json';
import type {NestedObjectType} from "../types.d.ts";
import {closest} from "./dom";
import {e} from "./basic";

const translations = {
    en: en,
    de: de,
    fr: fr
} as const;

export type TranslationKey = keyof NestedObjectType<typeof en>;
export type AvailableLanguages = keyof typeof translations;

// Default language
let currentLanguage: AvailableLanguages = 'en';

// Initialize language based on browser settings
function initializeLanguage(): void {
    // Use navigator.languages (array of preferred languages) if available, otherwise fall back to navigator.language
    const preferredLanguages = document.documentElement.hasAttribute('lang') ? [document.documentElement.getAttribute('lang') || 'en'] : navigator.languages || [navigator.language];

    // Try each language in order of preference
    for (const lang of preferredLanguages) {
        const browserLang = lang.split('-')[0].toLowerCase().substring(0, 2);
        if (browserLang in translations) {
            currentLanguage = browserLang as AvailableLanguages;
            return;
        }
    }

    // If no matching language found, keep the default 'en'
}

// Get a nested property from an object using a dot-notation path
function getNestedProperty<T, K extends keyof NestedObjectType<T>>(obj: T, path: K): NestedObjectType<T>[K] | undefined {
    const pathParts = path.split('.');
    let result: any = obj;

    for (const pathPart of pathParts) {
        if (typeof result === 'object' && result !== null) {
            result = result[pathPart];
        } else {
            return undefined;
        }
    }

    return result as NestedObjectType<T>[K];
}

// Replace placeholders in a string with actual values
function replacePlaceholders(text: string, params: Record<string, string | number> = {}): string {
    return text.replace(/{([^{}]*)}/g, (match, key) => {
        const value = params[key];
        return typeof value !== 'undefined' ? e(String(value)) : match;
    });
}

// Translate a string using the current language
export function translate(lang: AvailableLanguages, key: TranslationKey, params: Record<string, string | number> = {}): string {
    const translationObj = translations[lang] || translations.en;
    const translatedText = getNestedProperty(translationObj as typeof translations.en, key);

    if (typeof translatedText === 'string') {
        return replacePlaceholders(translatedText, params);
    }

    // Fallback to English if translation not found
    const fallbackText = getNestedProperty(translations.en, key);
    if (typeof fallbackText === 'string') {
        return replacePlaceholders(fallbackText, params);
    }

    // Return the key if no translation found
    return key;
}

export function getLanguage(el: Element): AvailableLanguages {
    const rawLang = closest(el, '[lang]')?.getAttribute('lang');
    if (rawLang) {
        const lang = rawLang.split('-')[0].toLowerCase().substring(0, 2)
        if (lang in translations) {
            return lang as AvailableLanguages;
        }
    }
    return currentLanguage

}

// Initialize language on load
if (typeof document === 'object') {
    initializeLanguage();
}
